import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const STORAGE_KEY_RESPONDIDA = 'pesquisa_opiniao_respondida';
// sessionStorage: zera a cada nova sessão (fechar/reabrir aba/navegador)
const SESSION_KEY_EXIBIDA = 'pesquisa_opiniao_exibida_sessao';

// Tempo mínimo e máximo em ms para exibir o banner (entre 1 e 2 minutos)
const MIN_DELAY_MS = 60_000;
const MAX_DELAY_MS = 120_000;

const OPCOES_OPINIAO = [
  { valor: 5, emoji: '😍', label: 'Adorei!' },
  { valor: 4, emoji: '😊', label: 'Gostei' },
  { valor: 3, emoji: '😐', label: 'Regular' },
  { valor: 2, emoji: '😕', label: 'Ruim' },
  { valor: 1, emoji: '😠', label: 'Péssimo' },
];

export default function BannerPesquisaOpiniao() {
  const [visivel, setVisivel] = useState(false);
  const [etapa, setEtapa] = useState<'banner' | 'pesquisa' | 'obrigado'>('banner');
  const [notaSelecionada, setNotaSelecionada] = useState<number | null>(null);
  const [comentario, setComentario] = useState('');
  const [enviando, setEnviando] = useState(false);

  const agendarExibicao = useCallback(() => {
    // Se já respondeu definitivamente, nunca mais exibe
    const jaRespondida = localStorage.getItem(STORAGE_KEY_RESPONDIDA);
    if (jaRespondida) return;

    // Se já foi exibido nessa sessão, não exibe de novo
    const jaExibidaNaSessao = sessionStorage.getItem(SESSION_KEY_EXIBIDA);
    if (jaExibidaNaSessao) return;

    const delay = MIN_DELAY_MS + Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS);
    const timer = setTimeout(() => {
      setVisivel(true);
      setEtapa('banner');
      setNotaSelecionada(null);
      setComentario('');
      // Marca que já foi exibido nessa sessão
      sessionStorage.setItem(SESSION_KEY_EXIBIDA, 'true');
    }, delay);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const cleanup = agendarExibicao();
    return cleanup;
  }, [agendarExibicao]);

  const handleFechar = () => {
    // Fecha sem reagendar — já foi exibido nessa sessão, só volta na próxima
    setVisivel(false);
  };

  const handleResponder = () => {
    setEtapa('pesquisa');
  };

  const handleEnviar = async () => {
    if (!notaSelecionada) return;
    setEnviando(true);

    try {
      const opcao = OPCOES_OPINIAO.find(o => o.valor === notaSelecionada);
      const formData = new URLSearchParams();
      formData.append('nota', String(notaSelecionada));
      formData.append('avaliacao', opcao?.label || '');
      formData.append('comentario', comentario);
      formData.append('data', new Date().toLocaleString('pt-BR'));

      await fetch('https://readdy.ai/api/form/d7ak50f6e3lk97049ua0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      localStorage.setItem(STORAGE_KEY_RESPONDIDA, 'true');
      setEtapa('obrigado');
      setTimeout(() => setVisivel(false), 3000);
    } catch {
      setEnviando(false);
    }
  };

  if (!visivel) return null;

  const modal = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(6px)',
        animation: 'pesquisaFadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          width: '90vw',
          maxWidth: '1400px',
          borderRadius: '32px',
          overflow: 'hidden',
          animation: 'pesquisaScaleIn 0.3s ease-out',
          boxShadow: '0 40px 100px rgba(0,0,0,0.5)',
        }}
      >
        {/* Banner principal */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 50%, #f59e0b 100%)',
          }}
        >
          {/* Blobs decorativos */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: 256, height: 256, background: 'white', borderRadius: '50%', filter: 'blur(60px)' }} />
            <div style={{ position: 'absolute', bottom: 0, right: 0, width: 384, height: 384, background: 'white', borderRadius: '50%', filter: 'blur(80px)' }} />
          </div>

          {/* Ícone decorativo */}
          <div style={{ position: 'absolute', top: 16, right: 56, color: 'rgba(255,255,255,0.15)', fontSize: 40 }}>
            <i className="ri-chat-smile-3-line"></i>
          </div>

          {/* Botão fechar */}
          <button
            onClick={handleFechar}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              cursor: 'pointer',
              zIndex: 10,
              color: 'white',
              fontSize: 18,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.35)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
          >
            <i className="ri-close-line"></i>
          </button>

          {/* ETAPA: Banner inicial */}
          {etapa === 'banner' && (
            <div style={{ position: 'relative', padding: '72px 80px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 72 }}>

                {/* Imagem */}
                <div style={{ flexShrink: 0, position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: -16, background: 'rgba(255,255,255,0.25)', borderRadius: 40, filter: 'blur(36px)' }} />
                  <div style={{
                    position: 'relative',
                    width: 560,
                    borderRadius: 28,
                    overflow: 'hidden',
                    border: '6px solid rgba(255,255,255,0.45)',
                    background: 'rgba(255,255,255,0.1)',
                  }}>
                    <img
                      src="https://storage.readdy-site.link/project_files/d3ef49a8-8963-4d4a-ac5c-718f7ab578ff/f32d8518-571e-4eab-872b-619762236ee5_getpoints_watsapp.png?v=ec6a4045f2f6819a47c60d8149ce51b4"
                      alt="GetPoints no WhatsApp"
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                </div>

                {/* Texto */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 18px',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: 999,
                    marginBottom: 16,
                  }}>
                    <i className="ri-star-fill" style={{ color: '#fde047', fontSize: 16 }}></i>
                    <span style={{ color: 'white', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em' }}>SUA OPINIÃO IMPORTA</span>
                  </div>

                  <h2 style={{ color: 'white', fontSize: 46, fontWeight: 900, lineHeight: 1.1, margin: '0 0 12px 0' }}>
                    O que você achou da{' '}
                    <span style={{ color: '#fde047' }}>novidade?</span>
                  </h2>

                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 17, margin: '0 0 32px 0', lineHeight: 1.5 }}>
                    Leva menos de 1 minuto e nos ajuda a melhorar sua experiência!
                  </p>

                  <div style={{ display: 'flex', gap: 14 }}>
                    <button
                      onClick={handleResponder}
                      style={{
                        padding: '14px 36px',
                        background: 'white',
                        color: '#ea580c',
                        borderRadius: 14,
                        fontWeight: 800,
                        fontSize: 16,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        whiteSpace: 'nowrap',
                        transition: 'transform 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.04)')}
                      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      <i className="ri-chat-smile-3-line"></i>
                      Avaliar agora
                    </button>
                    <button
                      onClick={handleFechar}
                      style={{
                        padding: '14px 26px',
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        borderRadius: 14,
                        fontWeight: 600,
                        fontSize: 15,
                        border: 'none',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                    >
                      Agora não
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ETAPA: Pesquisa */}
          {etapa === 'pesquisa' && (
            <form
              data-readdy-form
              onSubmit={(e) => { e.preventDefault(); handleEnviar(); }}
              style={{ position: 'relative', padding: '72px 80px' }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 24px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: 999,
                  marginBottom: 24,
                }}>
                  <i className="ri-star-fill" style={{ color: '#fde047', fontSize: 22 }}></i>
                  <span style={{ color: 'white', fontWeight: 700, fontSize: 18, letterSpacing: '0.06em' }}>AVALIAÇÃO RÁPIDA</span>
                </div>

                <h2 style={{ color: 'white', fontSize: 52, fontWeight: 900, lineHeight: 1.15, margin: '0 0 36px 0' }}>
                  Como você avalia essa nova modalidade?
                </h2>

                {/* Emojis */}
                <div style={{ display: 'flex', gap: 20, marginBottom: 36 }}>
                  {OPCOES_OPINIAO.map((opcao) => (
                    <button
                      key={opcao.valor}
                      type="button"
                      onClick={() => setNotaSelecionada(opcao.valor)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 10,
                        padding: '20px 28px',
                        borderRadius: 20,
                        border: 'none',
                        cursor: 'pointer',
                        background: notaSelecionada === opcao.valor ? 'white' : 'rgba(255,255,255,0.2)',
                        transform: notaSelecionada === opcao.valor ? 'scale(1.14)' : 'scale(1)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => {
                        if (notaSelecionada !== opcao.valor) e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                      }}
                      onMouseLeave={e => {
                        if (notaSelecionada !== opcao.valor) e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                      }}
                    >
                      <span style={{ fontSize: 48 }}>{opcao.emoji}</span>
                      <span style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: notaSelecionada === opcao.valor ? '#ea580c' : 'white',
                      }}>
                        {opcao.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Comentário */}
                <div style={{ marginBottom: 36 }}>
                  <textarea
                    name="comentario"
                    value={comentario}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setComentario(e.target.value);
                    }}
                    placeholder="Quer nos contar mais? (opcional)"
                    rows={3}
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderRadius: 16,
                      padding: '20px 24px',
                      fontSize: 18,
                      resize: 'none',
                      border: '1px solid rgba(255,255,255,0.25)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'right', margin: '8px 0 0 0' }}>
                    {comentario.length}/500
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 20 }}>
                  <button
                    type="submit"
                    disabled={!notaSelecionada || enviando}
                    style={{
                      padding: '20px 48px',
                      background: notaSelecionada ? 'white' : 'rgba(255,255,255,0.4)',
                      color: '#ea580c',
                      borderRadius: 16,
                      fontWeight: 800,
                      fontSize: 20,
                      border: 'none',
                      cursor: notaSelecionada ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      whiteSpace: 'nowrap',
                      opacity: notaSelecionada ? 1 : 0.6,
                    }}
                  >
                    {enviando ? (
                      <><i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }}></i>Enviando...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i>Enviar avaliação</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleFechar}
                    style={{
                      padding: '20px 36px',
                      background: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      borderRadius: 16,
                      fontWeight: 600,
                      fontSize: 18,
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* ETAPA: Obrigado */}
          {etapa === 'obrigado' && (
            <div style={{ position: 'relative', padding: '100px 80px', textAlign: 'center' }}>
              <div style={{ fontSize: 100, marginBottom: 24, animation: 'pesquisaBounce 0.6s ease-out' }}>🎉</div>
              <h2 style={{ color: 'white', fontSize: 52, fontWeight: 900, margin: '0 0 16px 0' }}>Obrigado pelo feedback!</h2>
              <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: 24, margin: 0 }}>Sua opinião nos ajuda a melhorar cada vez mais.</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pesquisaFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pesquisaScaleIn {
          from { opacity: 0; transform: scale(0.94) translateY(16px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes pesquisaBounce {
          0%, 100% { transform: translateY(0); }
          40% { transform: translateY(-20px); }
          60% { transform: translateY(-10px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
}
